import { FC, useCallback, useMemo } from 'react'
import { useAppDispatch } from '../redux/hooks'
import { useRouter } from 'next/router'
import Script from 'next/script'

import { BaseTransport, RegionLandingPageTransport } from '../transports'
import { RegionLandingPageParamsTransport } from '../transports/region-landing-page-transport'
import FAQTransport from '../transports/common/faq-transport'
import { deleteSession } from '../utils/delete-session'
import { saveHistoryItem } from '../redux/searchbar'
import Page from '../components/common/page'
import { LandingPage } from '../components'
import { getLandingPageUrlFromRoute } from '../utils/route-utils'
import { useTranslation } from '../hooks/locale'
import { fastlaneClient, pageClient } from '../clients'
import { ResponseLocals, PageProps } from '../common/types'
import { GetServerSideProps } from '../redux/types'
import { getMessagesFromLanguage, getLocaleFromLanguage } from '../i18n/utils'
import { getRoutesFromDomain } from '../routes/utils'
import { useUserDetails } from '../hooks/users'
import { BaseSearchbarHistoryItem } from '../redux/searchbar/types'
import { useSetWishlistCount } from '../hooks/wishlist'
import { modifyCanonicalOptionally } from '../utils/seo-utils'
import { getProductSchemaHTML } from '../schema/region-landing-page'
import { getFAQSchemaHTML } from '../schema/common'
import AdvancedBreadcrumbTransport from '../transports/common/advanced-breadcrumb-transport'
import { BreadcrumbTypeEnum } from '../common/enum'
import { generateHeroImagePreloadLinks, heroSectionToBackgroundImages } from '../components/common/hero-section/utils'
import { buildTemplateVariables } from '../utils/template-variables'
import { processPageTemplates, processRegionOverviewSection } from '../utils/template-processor'

type Props = {
    baseLandingPageTransport: BaseTransport<RegionLandingPageTransport>
} & PageProps

const _getLocationName = (
    breadcrumbs: AdvancedBreadcrumbTransport[],
    continentName: string,
    countryName?: string,
    regionName?: string,
    subregionName?: string,
) => {
    const lastRegion = [...breadcrumbs]
        .reverse()
        .find(b => b.type === BreadcrumbTypeEnum.REGION || b.type === BreadcrumbTypeEnum.ATTRACTION)

    return subregionName || regionName || countryName || continentName || lastRegion?.label || ''
}

const LandingPageContainer: FC<Props> = ({ baseLandingPageTransport }) => {
    const { data: landingPageTransport } = baseLandingPageTransport
    const {
        pageSubTypeResponse,
        staticData,
        templateData,
        head,
        hero,
        recommendedObjects,
        popularDestinations,
        topRatedObjects,
        topOffers,
        typicalAccommodations,
        objectsByPopularFilters,
        popularFilters,
        regions,
        holidayIdeas,
        breadcrumbs,
        directoryPageResponse,
        trustBar,
    } = landingPageTransport as RegionLandingPageTransport

    const router = useRouter()
    useSetWishlistCount()
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { userDetails } = useUserDetails({ additionalDetails: true })
    const isLoggedIn = !!userDetails?.id

    const addToHistory = useCallback(
        (historyItem: BaseSearchbarHistoryItem) => dispatch(saveHistoryItem(historyItem)),
        [dispatch],
    )

    const DEPRECATED_searchFunction = useCallback(
        async ({
            arrival,
            departure,
            persons,
            search,
            regionId,
        }: {
            arrival: string
            departure: string
            persons: number
            search: string
            regionId: number
        }) => {
            try {
                const response = await fastlaneClient.getSearchUrl({ arrival, departure, persons, regionId, search })
                document.location.href = `${t('traumBaseUrl')}${response.data?.searchurl}`
            } catch {}
        },
        [t],
    )

    const productSchemaHTML = useMemo(() => getProductSchemaHTML(landingPageTransport), [landingPageTransport])
    const faqSchemaHTML = useMemo(() => {
        // Priority to staticData.faqs
        let faqsToUse: FAQTransport[] = []
        if (staticData?.faqs && staticData.faqs.length > 0) {
            faqsToUse = staticData.faqs
        } else if (templateData?.faqSection?.questionAnswers && templateData?.faqSection?.questionAnswers?.length > 0) {
            faqsToUse = templateData.faqSection.questionAnswers.filter(qa => qa.question && qa.answer)
        }
        return getFAQSchemaHTML(faqsToUse)
    }, [staticData?.faqs, templateData?.faqSection?.questionAnswers])

    const { heroBackgroundImages, heroImagePreloadLinks } = useMemo(() => {
        const backgroundImages = heroSectionToBackgroundImages(hero)
        return {
            heroBackgroundImages: backgroundImages,
            heroImagePreloadLinks: generateHeroImagePreloadLinks(backgroundImages),
        }
    }, [hero])

    if (!landingPageTransport) {
        return null
    }

    const url = getLandingPageUrlFromRoute(router.query)
    const { continentName, countryName, regionName, subregionName } = staticData
    const locationName = _getLocationName(breadcrumbs, continentName, countryName, regionName, subregionName)
    const { head: staticHead } = staticData

    return (
        <Page head={head} pageHead={staticHead} imagePreloadLinks={heroImagePreloadLinks}>
            {!!productSchemaHTML && (
                <Script id="product-schema" type="application/ld+json" dangerouslySetInnerHTML={productSchemaHTML} />
            )}
            {!!faqSchemaHTML && (
                <Script id="faq-schema" type="application/ld+json" dangerouslySetInnerHTML={faqSchemaHTML} />
            )}
            <LandingPage
                url={url}
                isLoggedIn={isLoggedIn}
                firstName={userDetails?.firstName}
                hero={hero}
                locationName={locationName}
                recommendedObjects={recommendedObjects}
                addToHistory={addToHistory}
                searchFunction={DEPRECATED_searchFunction}
                pageSubTypeResponse={pageSubTypeResponse}
                staticData={staticData}
                templateData={templateData}
                popularDestinationsSection={popularDestinations}
                topRatedObjectsSection={topRatedObjects}
                topOfferListingsSection={topOffers}
                typicalAccommodationsSection={typicalAccommodations}
                popularFilterListingsSections={objectsByPopularFilters}
                popularFiltersSection={popularFilters}
                regionsSection={regions}
                holidayIdeasSection={holidayIdeas}
                breadcrumbs={breadcrumbs}
                directoryPageResponse={directoryPageResponse}
                trustBar={trustBar}
                heroBackgroundImages={heroBackgroundImages}
            />
        </Page>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req, res }) => {
    const { user, device } = res.locals as ResponseLocals

    const { continent, country, region, subregion } = req.params as unknown as RegionLandingPageParamsTransport
    const params: RegionLandingPageParamsTransport = { continent, country }
    const locale = getLocaleFromLanguage(user.language)
    const path = req.originalUrl?.split('?')[0] || req.path
    params.requestPath = path
    if (region) {
        params.region = region
    }
    if (subregion) {
        params.subregion = subregion
    }

    const data = await pageClient.getRegionLandingPage({ user, params })
    if (data.status === 404) {
        return {
            notFound: true,
        }
    }

    if (data.data?.templateData || data.data?.staticData?.regionOverviewSection) {
        const globalVariables = buildTemplateVariables(data.data, locale)

        if (data.data.templateData) {
            data.data.templateData = processPageTemplates(data.data.templateData, globalVariables)
        }

        if (data.data.staticData?.regionOverviewSection) {
            data.data.staticData.regionOverviewSection = processRegionOverviewSection(
                data.data.staticData.regionOverviewSection,
                globalVariables,
            )
        }
    }

    modifyCanonicalOptionally(req, data)
    const requestUserConfig = deleteSession(user)
    return {
        props: {
            baseLandingPageTransport: data,
            device,
            requestUserConfig: requestUserConfig,
            messages: await getMessagesFromLanguage(user.language),
            routes: await getRoutesFromDomain(user.domain),
            originalPath: req.originalPath,
            originalQuery: req.originalQuery,
            originalUrl: req.originalUrl,
        },
    }
}

export default LandingPageContainer
